import {
  capitalize,
  fromEventTarget,
  timestampToDateString,
  useClickOutsideEvent,
  useDerivedState,
  useModal,
} from '@/utils'
import { CalendarDays } from 'lucide-react'
import { useCallback, useRef, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { InputWithAddon } from '../../atoms'
import { WholeWidth } from '../../styled'
import { Calendar } from '../Calendar'
import { ModalBase } from '../ModalBase'
import { DateInput } from './styles'

const useMessages = () => {
  const { t } = useTranslation()

  return { done: t('done') }
}

export interface DatePickerProps {
  onChange: (value: number) => void
  value: number
  min?: string
  max?: string
  calendarHeader?: string
  [key: string]: any
}

export const DatePicker = ({
  onChange,
  value,
  min = '1000-01-01',
  max = '2200-01-01',
  calendarHeader,
  ...props
}: DatePickerProps) => {
  const messages = useMessages()
  const inputRef = useRef<HTMLInputElement>(null)
  const { isOpen, toggle, close } = useModal()
  const [calendarValue, setCalendarValue] = useDerivedState(value)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const parsedDate = fromEventTarget(Date.parse)(e)
    if (parsedDate === undefined || isNaN(parsedDate)) {
      return onChange(NaN)
    }

    if (Date.parse(min) > parsedDate || Date.parse(max) < parsedDate) {
      return onChange(NaN)
    }

    const handler = fromEventTarget((v) => onChange(Date.parse(v)))
    return handler(e)
  }

  const handleCalendarChange = () => {
    // typing to number deliberately because didn't want to change the logic
    const calendarValueNumber = calendarValue as number
    onChange(calendarValueNumber)
    toggle()
    if (inputRef.current) {
      inputRef.current.value = timestampToDateString(calendarValueNumber)
    }
  }

  useClickOutsideEvent(
    inputRef.current,
    () => {
      if (!value && inputRef.current) inputRef.current.value = ''
    },
    [value],
  )

  const DateComponent = useCallback(
    () => (
      <DateInput
        ref={inputRef}
        defaultValue={timestampToDateString(value)}
        max={max}
        min={min}
        onChange={handleChange}
        {...props}
      />
    ),
    [min, max],
  )
  return (
    <WholeWidth>
      <InputWithAddon
        addonAfter={() => <CalendarDays onClick={toggle} />}
        inputComponent={DateComponent}
      />
      {isOpen && (
        <ModalBase
          closable
          header={calendarHeader}
          isOpen={isOpen}
          onClose={close}
          onPrimaryClick={handleCalendarChange}
          primaryText={capitalize(messages.done)}
        >
          <Calendar
            maxDate={new Date(max)}
            minDate={new Date(min)}
            onChange={setCalendarValue}
            value={calendarValue as number}
          />
        </ModalBase>
      )}
    </WholeWidth>
  )
}
